// Workflow panel of one version: progress + the transitions the server allows
// for the current user + publish. Shared by the creative and version pages.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { keys } from "@/api/hooks";
import type { CreativeProduct, CreativeVersionSummary, Distribution, VersionState } from "@/api/types";
import { VERSION_STATES } from "@/lib/labels";
import { ConfirmDialog } from "@/ui/dialog";
import { TransitionActions, VersionStepper } from "@/components/workflow";
import { notifyError } from "../common/notify";
import { PublishDialog } from "./publish-dialog";

export function VersionPanel({
  orgId,
  version,
  distributions,
  canCreateDistribution,
  product,
}: {
  orgId: string;
  /** The creative's product (capability assessed for platform compatibility). */
  product: CreativeProduct | undefined;
  version: CreativeVersionSummary;
  distributions: Distribution[];
  canCreateDistribution: boolean;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const transition = async (to: Exclude<VersionState, "DRAFT" | "PUBLISHED">) => {
    setPending(true);
    try {
      const updated = await call(() =>
        api.POST("/orgs/{orgId}/versions/{versionId}/transitions", {
          params: { path: { orgId, versionId: version.id } },
          body: { to },
        })
      );
      toast.success(`Versión ${String(updated.versionNumber)}: ${VERSION_STATES[updated.state].label}`);
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setPending(false);
      setConfirmArchive(false);
    }
  };

  return (
    <div className="grid gap-3">
      <VersionStepper state={version.state} />
      <TransitionActions
        allowed={version.allowedTransitions}
        pending={pending}
        onTransition={(to) => {
          if (to === "ARCHIVED") setConfirmArchive(true);
          else void transition(to);
        }}
        onPublish={() => {
          setPublishOpen(true);
        }}
      />
      {version.allowedTransitions.length === 0 && version.state !== "ARCHIVED" ? (
        <p className="text-xs text-muted">No tienes acciones disponibles en esta versión.</p>
      ) : null}
      <PublishDialog
        key={`${version.id}-${String(publishOpen)}`}
        orgId={orgId}
        creativeId={version.creativeId}
        product={product}
        versionId={version.id}
        versionNumber={version.versionNumber}
        distributions={distributions}
        canCreateDistribution={canCreateDistribution}
        open={publishOpen}
        onOpenChange={setPublishOpen}
      />
      <ConfirmDialog
        open={confirmArchive}
        onOpenChange={setConfirmArchive}
        title={`¿Archivar la versión ${String(version.versionNumber)}?`}
        description="Una versión archivada no se puede editar, publicar ni activar. Las demás versiones no cambian."
        confirmLabel="Archivar versión"
        destructive
        pending={pending}
        onConfirm={() => void transition("ARCHIVED")}
      />
    </div>
  );
}
